import {
  AppleLogo,
  ArrowElbowDownLeft,
  ArrowsDownUp,
  Books,
  ChartLineUp,
  Check,
  Compass,
  CrownSimple,
  GooglePlayLogo,
  MagnifyingGlass,
  Pause,
  Play,
  Rss,
  SkipBack,
  SkipForward,
  SunHorizon,
  X,
} from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router';
import { searchStories } from '@/lib/api/content';
import { getSymbols } from '@/lib/api/markets';
import { searchSources } from '@/lib/api/user';
import { useAuth } from '@/lib/auth';
import { ANDROID_URL, IOS_URL, MOD, timeAgo } from '@/lib/format';
import { useCategories, useCategoryLookup } from '@/lib/hooks';
import { useT, useUiLang } from '@/lib/i18n';
import { onCheckoutCompleted, paddleEnabled } from '@/lib/paddle';
import { player, usePlayer } from '@/lib/player';
import { categoryColor, categoryLabel } from '@/lib/taxonomy';
import { ui, useUi } from '@/lib/ui';
import { Img, Modal, SourceLogo } from './primitives';

export function Overlays() {
  const s = useUi();
  return (
    <>
      {s.palette && <CommandPalette />}
      {s.premium && <PremiumModal />}
      {s.shortcuts && <ShortcutsModal />}
      <PlayerBar />
      <div className="toasts" aria-live="polite">
        {s.toasts.map((t) => (
          <div className="toast" key={t.id}>
            {t.text}
          </div>
        ))}
      </div>
    </>
  );
}

// ---------- Komut paleti ----------

type PItem = { key: string; group: string; title: string; sub?: string; icon: ReactNode; to: string };

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

function CommandPalette() {
  const t = useT();
  const lang = useUiLang();
  const nav = useNavigate();
  const { contentLang } = useAuth();
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const dq = useDebounced(q.trim(), 220);
  const listRef = useRef<HTMLDivElement>(null);
  const { data: cats } = useCategories();
  const catLookup = useCategoryLookup();
  const symbolsQ = useQuery({ queryKey: ['symbols'], queryFn: getSymbols, staleTime: Infinity });
  const storiesQ = useQuery({ queryKey: ['search', dq, contentLang], enabled: dq.length >= 2, queryFn: () => searchStories(dq, contentLang, 8) });
  const sourcesQ = useQuery({ queryKey: ['searchSources', dq], enabled: dq.length >= 2, queryFn: () => searchSources(dq, 5) });

  const items = useMemo<PItem[]>(() => {
    const out: PItem[] = [];
    const needle = dq.toLocaleLowerCase(lang);
    const pages: PItem[] = [
      { key: 'p-d', group: t('search_pages'), title: t('nav_discover'), icon: <Compass size={18} />, to: '/' },
      { key: 'p-t', group: t('search_pages'), title: t('nav_today'), icon: <SunHorizon size={18} />, to: '/today' },
      { key: 'p-m', group: t('search_pages'), title: t('nav_markets'), icon: <ChartLineUp size={18} />, to: '/markets' },
      { key: 'p-l', group: t('search_pages'), title: t('nav_library'), icon: <Books size={18} />, to: '/library' },
      { key: 'p-s', group: t('search_pages'), title: t('nav_sources'), icon: <Rss size={18} />, to: '/sources' },
    ];
    const catItems: PItem[] = (cats ?? []).map((c) => ({
      key: `c-${c.id}`,
      group: t('search_categories'),
      title: categoryLabel(lang, c.slug, c.name),
      icon: <span className="dot" style={{ '--c': categoryColor(c.slug), width: 10, height: 10 } as CSSProperties} />,
      to: `/c/${c.slug}`,
    }));
    if (!needle) return [...pages, ...catItems];
    out.push(...(storiesQ.data ?? []).map((s) => ({
      key: `s-${s.id}`,
      group: t('search_stories'),
      title: s.heading,
      sub: [catLookup.name(s.category_id), timeAgo(s.published_at ?? s.first_seen_at)].filter(Boolean).join(' · '),
      icon: <Img src={s.cover_image_url} color={catLookup.color(s.category_id)} style={{ width: 32, height: 32 }} width={40} />,
      to: `/s/${s.id}`,
    })));
    out.push(...catItems.filter((c) => c.title.toLocaleLowerCase(lang).includes(needle)));
    out.push(
      ...(symbolsQ.data ?? [])
        .filter((s) => s.symbol.toLowerCase().includes(needle) || s.display_name.toLocaleLowerCase(lang).includes(needle))
        .slice(0, 5)
        .map((s) => ({
          key: `m-${s.symbol}`,
          group: t('search_symbols'),
          title: s.display_name,
          sub: s.symbol,
          icon: <ChartLineUp size={18} />,
          to: `/markets/${encodeURIComponent(s.symbol)}`,
        }))
    );
    out.push(...(sourcesQ.data ?? []).map((s) => ({
      key: `r-${s.id}`,
      group: t('search_sources'),
      title: s.title,
      sub: t(`src_kind_${s.kind}` as 'src_kind_news_rss'),
      icon: <SourceLogo src={s.logo_url} site={s.site_url} className="src-logo" />,
      to: `/source/${s.id}`,
    })));
    out.push(...pages.filter((p) => p.title.toLocaleLowerCase(lang).includes(needle)));
    return out;
  }, [dq, cats, storiesQ.data, sourcesQ.data, symbolsQ.data, lang, t, catLookup]);

  useEffect(() => setActive(0), [dq]);
  useEffect(() => {
    listRef.current?.querySelector('.palette-item.active')?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const go = (it: PItem | undefined) => {
    if (!it) return;
    ui.closePalette();
    nav(it.to);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(items.length - 1, a + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(items[active]);
    } else if (e.key === 'Escape') {
      ui.closePalette();
    }
  };

  const loading = dq.length >= 2 && (storiesQ.isFetching || sourcesQ.isFetching);
  let lastGroup = '';
  return createPortal(
    <>
      <div className="overlay" onClick={ui.closePalette} />
      <div className="palette" role="dialog" aria-modal="true" aria-label={t('nav_search')}>
        <div className="palette-input">
          <MagnifyingGlass size={20} className="muted" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder={t('search_placeholder')}
            aria-label={t('search_placeholder')}
            role="combobox"
            aria-expanded="true"
            aria-controls="palette-list"
          />
          {loading && <span className="muted" style={{ fontSize: 12 }}>{t('loading')}</span>}
          <button className="icon-btn icon-btn-sm" onClick={ui.closePalette} aria-label={t('close')}>
            <X size={16} />
          </button>
        </div>
        <div className="palette-list" ref={listRef} id="palette-list" role="listbox">
          {items.length === 0 && dq.length >= 2 && !loading && (
            <p className="muted" style={{ padding: 20, textAlign: 'center' }}>
              {t('search_empty', { q: dq })}
            </p>
          )}
          {items.map((it, i) => {
            const head = it.group !== lastGroup;
            lastGroup = it.group;
            return (
              <div key={it.key}>
                {head && <div className="palette-group">{it.group}</div>}
                <button
                  role="option"
                  aria-selected={i === active}
                  className={`palette-item${i === active ? ' active' : ''}`}
                  onMouseMove={() => setActive(i)}
                  onClick={() => go(it)}
                >
                  <span className="pi-icon">{it.icon}</span>
                  <span className="pi-main">
                    <span className="pi-title">{it.title}</span>
                    {it.sub && <span className="pi-sub">{it.sub}</span>}
                  </span>
                </button>
              </div>
            );
          })}
        </div>
        <div className="palette-foot">
          <span>
            <ArrowsDownUp size={14} /> {t('search_hint')}
          </span>
          <span>
            <ArrowElbowDownLeft size={14} /> {t('search_open')}
          </span>
          <span>
            <span className="kbd">esc</span> {t('close')}
          </span>
        </div>
      </div>
    </>,
    document.body
  );
}

// ---------- Premium ----------

function PremiumModal() {
  const t = useT();
  const nav = useNavigate();
  const { user, refresh, premium } = useAuth();
  const [checking, setChecking] = useState(false);
  const [plan, setPlan] = useState<'yearly' | 'monthly'>('yearly');
  const [phase, setPhase] = useState<'idle' | 'activating' | 'error'>('idle');
  useEffect(() => {
    if (premium) ui.closePremium();
  }, [premium]);

  // Ödeme bitince Adapty webhook'u + sync-premium birkaç saniye sürebilir: 60 sn boyunca yokla.
  useEffect(
    () =>
      onCheckoutCompleted(() => {
        setPhase('activating');
        let n = 0;
        const tick = async () => {
          n += 1;
          await refresh();
          if (n < 20) timer = window.setTimeout(tick, 3000);
          else setPhase('error');
        };
        let timer = window.setTimeout(tick, 2500);
        return () => window.clearTimeout(timer);
      }),
    [refresh]
  );

  const buy = () => {
    if (!user) return;
    ui.closePremium();
    nav(`/checkout?plan=${plan}`);
  };

  return (
    <Modal onClose={ui.closePremium} labelledBy="prem-title">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <span className="badge badge-premium">
          <CrownSimple size={13} weight="fill" /> {t('prem_badge')}
        </span>
        <button className="icon-btn icon-btn-sm" onClick={ui.closePremium} aria-label={t('close')}>
          <X size={16} />
        </button>
      </div>
      <h2 id="prem-title" style={{ marginTop: 14 }}>
        {t('prem_title')}
      </h2>
      <p className="dim" style={{ marginTop: 8 }}>
        {paddleEnabled ? t('prem_body_web') : t('prem_body')}
      </p>
      <ul style={{ marginTop: 18, display: 'grid', gap: 10 }}>
        {(['prem_f1', 'prem_f2', 'prem_f3', 'prem_f4'] as const).map((k) => (
          <li key={k} style={{ display: 'flex', gap: 10, alignItems: 'center', fontWeight: 550 }}>
            <span style={{ width: 22, height: 22, borderRadius: 7, display: 'grid', placeItems: 'center', background: 'var(--accent-soft)', color: 'var(--accent-strong)' }}>
              <Check size={13} weight="bold" />
            </span>
            {t(k)}
          </li>
        ))}
      </ul>

      {paddleEnabled && (
        <>
          <div className="plan-picker" role="radiogroup" aria-label={t('prem_badge')}>
            {(['yearly', 'monthly'] as const).map((p) => (
              <button key={p} role="radio" aria-checked={plan === p} className={`plan${plan === p ? ' on' : ''}`} onClick={() => setPlan(p)}>
                <span className="plan-name">
                  {p === 'yearly' ? t('prem_yearly') : t('prem_monthly')}
                  {p === 'yearly' && <span className="badge badge-premium">{t('prem_save')}</span>}
                </span>
                <span className="plan-price">{p === 'yearly' ? t('prem_price_yearly') : t('prem_price_monthly')}</span>
                {p === 'yearly' && <span className="plan-sub">{t('prem_price_yearly_sub')}</span>}
              </button>
            ))}
          </div>
          {user ? (
            <button className="btn btn-brand btn-lg btn-block" style={{ marginTop: 14 }} onClick={buy} disabled={phase === 'activating'}>
              {phase === 'activating' ? t('prem_activating') : t('prem_continue')}
            </button>
          ) : (
            <Link className="btn btn-brand btn-lg btn-block" style={{ marginTop: 14 }} to={`/signin?next=${encodeURIComponent(location.pathname)}`} onClick={ui.closePremium}>
              {t('prem_signin')}
            </Link>
          )}
          {phase === 'error' && (
            <p role="alert" style={{ color: 'var(--down)', fontSize: 13.5, marginTop: 8 }}>
              {t('prem_pending')}
            </p>
          )}
          <p className="muted" style={{ fontSize: 12, marginTop: 10, textAlign: 'center' }}>
            {t('prem_legal')}{' '}
            <Link to="/pricing" onClick={ui.closePremium} style={{ textDecoration: 'underline' }}>
              {t('sub_see_plans')}
            </Link>
          </p>
          <div className="divider">{t('prem_or_app')}</div>
        </>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: paddleEnabled ? 0 : 22 }}>
        <a className={`btn ${paddleEnabled ? 'btn-soft' : 'btn-primary'} btn-lg`} href={IOS_URL} target="_blank" rel="noreferrer">
          <AppleLogo size={18} weight="fill" /> {t('prem_ios')}
        </a>
        <a className="btn btn-soft btn-lg" href={ANDROID_URL} target="_blank" rel="noreferrer">
          <GooglePlayLogo size={18} weight="fill" /> {t('prem_android')}
        </a>
      </div>
      {user && (
        <div style={{ marginTop: 14, textAlign: 'center' }}>
          <button
            className="btn btn-ghost btn-sm"
            disabled={checking}
            onClick={async () => {
              setChecking(true);
              await refresh();
              setChecking(false);
            }}
          >
            {checking ? t('loading') : `${t('prem_have')} · ${t('prem_refresh')}`}
          </button>
        </div>
      )}
    </Modal>
  );
}

// ---------- Kısayollar ----------

function ShortcutsModal() {
  const t = useT();
  const rows: [string[], string][] = [
    [[MOD, 'K'], t('kb_search')],
    [['J', 'K'], t('kb_next_story')],
    [['Enter'], t('kb_open')],
    [['←', '→'], t('kb_cards')],
    [['F'], t('kb_full')],
    [['S'], t('kb_save')],
    [['L'], t('kb_listen')],
    [['A'], t('kb_ask')],
    [['G', 'D'], t('kb_go_discover')],
    [['G', 'B'], t('kb_go_today')],
    [['?'], t('kb_help')],
  ];
  return (
    <Modal onClose={ui.closeShortcuts} labelledBy="kb-title">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 id="kb-title">{t('kb_title')}</h2>
        <button className="icon-btn icon-btn-sm" onClick={ui.closeShortcuts} aria-label={t('close')}>
          <X size={16} />
        </button>
      </div>
      <div style={{ marginTop: 16 }}>
        {rows.map(([keys, label]) => (
          <div className="set-row" key={label} style={{ padding: '10px 0' }}>
            <span>{label}</span>
            <span style={{ display: 'flex', gap: 4 }}>
              {keys.map((k) => (
                <span className="kbd" key={k}>
                  {k}
                </span>
              ))}
            </span>
          </div>
        ))}
      </div>
    </Modal>
  );
}

// ---------- Oynatıcı ----------

function PlayerBar() {
  const t = useT();
  const p = usePlayer();
  const cats = useCategoryLookup();
  const track = p.queue[p.index];
  useEffect(() => {
    document.documentElement.style.setProperty('--player-h', track ? '84px' : '0px');
  }, [track]);
  if (!track) return null;
  const pct = p.mode === 'speech' && p.parts > 0 ? (p.part / p.parts) * 100 : p.playing ? 50 : 0;
  return (
    <div className="player" role="region" aria-label={t('player_now')}>
      <Img src={track.image} color={cats.color(null)} width={56} />
      <Link to={`/s/${track.contentId}`} style={{ minWidth: 0 }}>
        <div className="player-sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {p.playing && (
            <span className="eq" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
          )}
          {t('player_now')}
          {p.queue.length > 1 && ` · ${p.index + 1}/${p.queue.length}`}
          {p.mode === 'speech' && <span className="hide-sm"> · {t('player_browser_voice')}</span>}
        </div>
        <div className="player-title">{track.title}</div>
        <div className="player-bar">
          <i style={{ width: `${pct}%` }} />
        </div>
      </Link>
      <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <button className="icon-btn hide-sm" onClick={player.prev} aria-label={t('player_prev')} disabled={p.index === 0}>
          <SkipBack size={18} weight="fill" />
        </button>
        <button className="icon-btn play" onClick={player.toggle} aria-label={p.playing ? t('story_pause') : t('story_listen')}>
          {p.loading ? <span className="eq"><i /><i /><i /></span> : p.playing ? <Pause size={18} weight="fill" /> : <Play size={18} weight="fill" />}
        </button>
        <button className="icon-btn hide-sm" onClick={player.next} aria-label={t('player_next')} disabled={p.index + 1 >= p.queue.length}>
          <SkipForward size={18} weight="fill" />
        </button>
        <button className="icon-btn" onClick={player.stop} aria-label={t('player_stop')}>
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
